import type { Suggestions, StorageSummary, WineDetail } from "@corklog/shared";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { createLot, fetchStorages, fetchSuggestions, fetchWine } from "../api.js";
import { useCellar, useMayWrite } from "../cellar.js";
import { BackToWines } from "../components/BackToWines.js";
import { BottlePlacement } from "../components/BottlePlacement.js";
import { actionButton, control, fieldLabel, resting } from "../components/controls.js";
import { DepartedBottles } from "../components/DepartedBottles.js";
import { TastedElsewhere } from "../components/TastedElsewhere.js";
import { DrinkingWindow } from "../components/DrinkingWindow.js";
import { LotList } from "../components/LotList.js";
import { OwnRating } from "../components/OwnRating.js";
import { WinePhotos } from "../components/WinePhotos.js";
import { Picker } from "../components/Picker.js";
import { AreaReportLinks } from "../components/AreaReportLinks.js";
import { WineFacts } from "../components/WineFacts.js";
import { WineRatings } from "../components/WineRatings.js";
import { ReplacedWindow } from "../components/ReplacedWindow.js";
import { WindowHelp } from "../components/WindowHelp.js";
import { WindowReasons } from "../components/WindowReasons.js";
import { useReload } from "../offline.js";

/**
 * One wine: what it is, what was bought, and where those bottles are standing.
 *
 * The three levels are visible as three sections, because they are three
 * different facts. The wine is what could be bought again; a lot is one
 * acquisition of it at one price on one date; a bottle is the object on the
 * shelf. Registering a lot creates its bottles, so the form that adds a lot is
 * also the form that puts bottles in the cabinet — there is no second step,
 * because in the world there is no second step either.
 */
const CURRENT_YEAR = new Date().getFullYear();

const FORMATS = ["Half", "Bottle", "Magnum", "Jeroboam", "Other"] as const;

type State =
  { status: "loading" } | { status: "ready"; wine: WineDetail } | { status: "failed" };

export function Wine() {
  const { t } = useTranslation();
  const { cellarId = "", wineId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const [storages, setStorages] = useState<StorageSummary[]>([]);
  // The cellar this wine is in, from the provider above the routes rather than
  // fetched here as it was until `9.2`. It carries two things this page needs:
  // the scale its own bottles are scored on — null until it arrives, and the
  // score box waits rather than guessing what the number would mean — and who
  // is reading, which decides whether the page has any buttons at all.
  const cellar = useCellar();
  const tastingScale = cellar?.tastingScale ?? null;
  const mayWrite = useMayWrite();
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);

  const [purchasedOn, setPurchasedOn] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("SEK");
  const [quantity, setQuantity] = useState("6");
  /**
   * Where the bottles were bought, in the cellar's own words since `9.11`.
   *
   * Empty rather than defaulting to Systembolaget: the picker below suggests
   * what this cellar has bought through before, most used first, so the usual
   * answer is one tap away — and a field pre-filled with a guess is a field
   * that quietly records the guess when nobody looks at it.
   */
  const [source, setSource] = useState("");
  /**
   * The wine's article number, as typed here — null until somebody touches the
   * field, which then shows what the wine already has.
   *
   * Null rather than a copy taken when the wine loads, because the page reloads
   * itself whenever the worker brings a fresher answer, and a copy would either
   * go stale or overwrite what is being typed. And only a touched field is
   * sent: the number belongs to the wine, and registering a lot without looking
   * at it must not write a value onto the wine that somebody else may have
   * changed meanwhile.
   */
  const [systembolagetId, setSystembolagetId] = useState<string | null>(null);
  const [format, setFormat] = useState<(typeof FORMATS)[number]>("Bottle");
  const [shelfId, setShelfId] = useState("");
  const [receivedFrom, setReceivedFrom] = useState("");
  const [promptedBy, setPromptedBy] = useState("");
  // A cellar has a past: wines bought and drunk before any of this existed are
  // worth a row, and worth more than a row saying only that they are gone.
  const [alreadyDrunk, setAlreadyDrunk] = useState(false);
  const [consumedOn, setConsumedOn] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Whether the lot form stands open. Null until somebody says, and then the
   * page decides for itself — open on a wine with no lots, folded on one that
   * has them.
   *
   * It is eleven fields, and it was open on every visit. A wine is bought once
   * or twice in its life and its page is read hundreds of times, so what stood
   * at rest under every bottle in the cellar was a purchase form nobody was
   * filling in. Folded, the page at rest is what the page is for: what the
   * wine is, whether it is ready, what is thought of it, and where its bottles
   * are.
   *
   * Open on a wine with no lots because that is the one moment it is the next
   * thing to do — registering a wine leaves for this page precisely so its
   * bottles can be entered — and because `design-direction.md` asks an empty
   * screen to invite the next action rather than to hide it behind a tap.
   */
  const [addingLot, setAddingLot] = useState<boolean | null>(null);

  const load = useCallback(() => {
    void fetchWine(cellarId, wineId)
      .then((wine) => setState({ status: "ready", wine }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId, wineId]);

  useReload(load);

  useEffect(() => {
    void fetchStorages(cellarId)
      .then((found) => {
        setStorages(found);
        const first = found.flatMap((storage) => storage.shelves)[0];
        if (first !== undefined) setShelfId((current) => current || first.id);
      })
      .catch(() => setStorages([]));
  }, [cellarId]);

  // Only for the rating form's source picker, and fetched here rather than
  // inside it so that opening the form is instant and so that a cellar whose
  // suggestions cannot be reached still shows every rating it holds.
  useEffect(() => {
    void fetchSuggestions(cellarId)
      .then(setSuggestions)
      .catch(() => setSuggestions(null));
  }, [cellarId]);

  // A price and its currency stand or fall together: 450 is not a price unless
  // something says whether it is kronor. A gift has neither.
  const priceGiven = price.trim() !== "";
  // Bottles still here need a shelf; bottles already drunk need the date they
  // were drunk on, and have no shelf to need. The server refuses both ways
  // round as well — this is only so the button can say so first.
  const placed = alreadyDrunk ? consumedOn !== "" : shelfId !== "";
  const canSubmit =
    placed &&
    Number(quantity) > 0 &&
    // Required since `9.11`, where it stopped being a dropdown that always had
    // an answer. A lot with no source is a purchase nobody said anything about.
    source.trim() !== "" &&
    !busy &&
    (!priceGiven || currency.trim() !== "");

  if (state.status !== "ready")
    return (
      <section>
        <BackToWines cellarId={cellarId} />
        {state.status === "loading" ? (
          <p className="text-muted text-sm">{t("loading")}</p>
        ) : (
          <p role="alert" className="text-past text-sm">
            {t("wine.loadFailed")}
          </p>
        )}
      </section>
    );

  const { wine } = state;
  // A bottle at a dinner is still owned, so it counts here. project-scope.md
  // is explicit about it: Away counts in the inventory, in the cellar value
  // and in the drinking-window views, and a count that read it as gone would
  // be the lie the status exists to avoid.
  const remaining = wine.bottles.filter(
    (bottle) => bottle.status === "InCellar" || bottle.status === "Away",
  );
  const gone = wine.bottles.filter(
    (bottle) => bottle.status !== "InCellar" && bottle.status !== "Away",
  );
  // Nobody has said, so the wine says: a page with no lots on it is a page
  // whose next act is registering one — unless the reader is a guest, for whom
  // there is no next act and a form that opened itself would be the loudest
  // thing on the page.
  const showLotForm = mayWrite === true && (addingLot ?? wine.lots.length === 0);

  return (
    <section>
      <BackToWines cellarId={cellarId} />

      <div className="flex items-baseline justify-between gap-4">
        <h1 className="font-display text-3xl">{wine.name}</h1>
        <span className="font-display text-muted text-2xl">{wine.number}</span>
      </div>
      <p className="text-muted mt-1">
        {wine.producer}
        {wine.region === null ? "" : `, ${wine.region}`} ·{" "}
        {wine.isNonVintage ? t("wines.nvShort") : (wine.vintage ?? "—")}
        {wine.style === null ? "" : ` · ${wine.style}`}
      </p>
      {/* The area report about the wine's region or its house, when one is
          published — 10.36. Under the line that names them, since that is
          where the eye already is. */}
      <AreaReportLinks
        producer={wine.producer}
        region={wine.region}
        countryCode={wine.countryCode}
        kind={wine.type}
      />
      {/* What the catalogue knows about the wine itself, labelled and read
          by everybody. The grapes and the alcohol were two lines of their own
          here until 2026-09-21 and have moved into the list beside the facts
          that had no home at all — the country, the type, and the three
          Champagne fields that could only be seen from inside the form that
          changes them. */}
      <WineFacts wine={wine} />

      {/* The one page that says where the years came from. A band drawn from
          the owner's own judgement and one drawn from a spreadsheet look the
          same, and this is the page where that difference is acted on. */}
      <DrinkingWindow
        years={{
          drinkFrom: wine.drinkFrom,
          peakFrom: wine.peakFrom,
          peakTo: wine.peakTo,
          drinkBy: wine.drinkBy,
        }}
        sources={{
          drinkFromSource: wine.drinkFromSource,
          peakFromSource: wine.peakFromSource,
          peakToSource: wine.peakToSource,
          drinkBySource: wine.drinkBySource,
        }}
        currentYear={CURRENT_YEAR}
      />

      {/* What the window used to say — the import's own years, which never
          move however many times these are saved, and what the last suggestion
          wrote over where that was something else. Small text, never a second
          band. */}
      <ReplacedWindow wine={wine} />

      {/* What was said about those years, and when. The years are on the wine
          above; the argument that produced them exists nowhere else, and it is
          the difference between a number that can be weighed and one that can
          only be believed. */}
      <WindowReasons suggestions={wine.windowSuggestions} />

      {/* Directly under the band, because it is the same question: the band
          says what the years mean, and this is how years get there at all.
          Nothing is asked until the button is pressed, and nothing is written
          until a year is taken. */}
      <WindowHelp cellarId={cellarId} wineId={wineId} wine={wine} onAccepted={load} />

      {/* Under the window, because those two are the pair a decision is made
          from: is it ready, and is it any good. */}
      <OwnRating
        cellarId={cellarId}
        wineId={wineId}
        scale={tastingScale}
        current={wine.ownRating}
        // Remounted on the wine's identity and its current reading, so the
        // field forgets a half-typed number once the saved one has arrived.
        key={`${wineId}-${wine.ownRating?.score ?? "none"}`}
        onSaved={load}
      />

      {/* Correcting a wine is the same ten fields registering it was, so it is
          the same form on a page of its own rather than a section that unfolds
          here. Not offered to a guest, who cannot correct anything. */}
      {mayWrite === true && (
        <Link
          to={`/kallare/${cellarId}/viner/${wineId}/andra`}
          className="text-accent mt-4 inline-block text-sm underline-offset-4 hover:underline"
        >
          {t("wine.edit")}
        </Link>
      )}

      <h2 className="font-display border-line mt-10 border-b pb-2 text-lg">
        {t("wine.ratings.heading")}
      </h2>
      <WineRatings
        cellarId={cellarId}
        wineId={wineId}
        ratings={wine.ratings}
        suggestions={suggestions}
        onChanged={load}
      />

      <h2 className="font-display border-line mt-10 border-b pb-2 text-lg">
        {t("wine.bottles", { remaining: remaining.length, total: wine.bottles.length })}
      </h2>
      {wine.bottles.length === 0 ? (
        <p className="text-muted mt-3 max-w-prose text-sm">{t("wine.noBottles")}</p>
      ) : (
        <BottlePlacement
          cellarId={cellarId}
          bottles={wine.bottles}
          storages={storages}
          onMoved={load}
        />
      )}

      {/* What has gone, and what was said about it. Below the bottles still
          standing, because the question asked in front of a cabinet is what is
          there — and above the lots, because a bottle drunk last Tuesday is
          nearer to hand than the invoice for it. */}
      {gone.length > 0 && (
        <>
          <h2 className="font-display border-line mt-10 border-b pb-2 text-lg">
            {t("wine.departed.heading", { count: gone.length })}
          </h2>
          <DepartedBottles
            cellarId={cellarId}
            bottles={wine.bottles}
            notes={wine.tastingNotes}
            onCorrected={load}
          />
        </>
      )}

      {/* `10.25`: what the wine was like when it was drunk somewhere else.
          Right after the bottles drunk here, because the two are one question
          — what has this wine been like — asked of two kinds of evening. */}
      <TastedElsewhere
        cellarId={cellarId}
        wineId={wineId}
        notes={wine.tastingNotes}
        onChanged={load}
      />

      {/* Above the acquisitions, because a picture answers *which bottle is
          this* and the lots answer *where did it come from* — the first is the
          question somebody standing at a cabinet is asking. */}
      <WinePhotos
        cellarId={cellarId}
        wineId={wineId}
        photos={wine.photos}
        onChanged={load}
      />

      <h2 className="font-display border-line mt-10 border-b pb-2 text-lg">
        {t("wine.lots")}
      </h2>
      {wine.lots.length === 0 ? (
        <p className="text-muted mt-3 max-w-prose text-sm">{t("wine.noLots")}</p>
      ) : (
        <LotList
          cellarId={cellarId}
          wineId={wineId}
          lots={wine.lots}
          systembolagetId={wine.systembolagetId}
          suggestions={suggestions}
          onDeleted={load}
        />
      )}

      {/* The door to the form, on the page that is read rather than written
          to. It says what it opens and nothing else — the form behind it keeps
          its own name on its own button, so the action keeps its name through
          the flow the way `design-direction.md` asks. */}
      {!showLotForm && mayWrite === true && (
        <button
          type="button"
          onClick={() => setAddingLot(true)}
          className="text-accent mt-4 inline-block text-sm underline-offset-4 hover:underline"
        >
          {t("wine.addLot")}
        </button>
      )}

      {showLotForm && (
        <form
          className="border-line mt-8 grid gap-4 border-t pt-6 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            setBusy(true);
            setError(null);
            void createLot(cellarId, wineId, {
              purchasedOn: purchasedOn === "" ? null : purchasedOn,
              pricePerBottle: priceGiven ? Number(price) : null,
              currency: priceGiven ? currency : null,
              quantity: Number(quantity),
              source,
              note: null,
              receivedFrom: receivedFrom.trim() === "" ? null : receivedFrom,
              promptedBy: promptedBy.trim() === "" ? null : promptedBy,
              format,
              shelfId: alreadyDrunk ? null : shelfId,
              consumedOn: alreadyDrunk ? consumedOn : null,
              ...(systembolagetId !== null && {
                systembolagetId: systembolagetId.trim() === "" ? null : systembolagetId,
              }),
            })
              .then(() => {
                setPrice("");
                setSystembolagetId(null);
                setReceivedFrom("");
                setPromptedBy("");
                setAlreadyDrunk(false);
                setConsumedOn("");
                // Folded again, and now there is a lot to fold behind: the page
                // goes back to being the page, with the parti that was just
                // registered standing in the list above.
                setAddingLot(false);
                load();
              })
              .catch(() => setError(t("wine.saveFailed")))
              .finally(() => setBusy(false));
          }}
        >
          <legend className="font-display col-span-full text-lg">
            {t("wine.addLot")}
          </legend>

          <LotField id="lot-qty" label={t("wine.quantity")}>
            <input
              id="lot-qty"
              inputMode="numeric"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className={`${control} ${resting}`}
            />
          </LotField>

          <LotField id="lot-format" label={t("wine.format")}>
            <select
              id="lot-format"
              value={format}
              onChange={(event) => setFormat(event.target.value as typeof format)}
              className={`${control} ${resting}`}
            >
              {FORMATS.map((value) => (
                <option key={value} value={value}>
                  {t(`format.${value}`)}
                </option>
              ))}
            </select>
          </LotField>

          {/* The shelf and the date it was drunk are the same question asked of
            two different lots — where are these bottles — so they occupy the
            same place in the form rather than sitting side by side, one of
            them always inapplicable. */}
          {alreadyDrunk ? (
            <LotField id="lot-consumed" label={t("wine.consumedOn")}>
              <input
                id="lot-consumed"
                type="date"
                value={consumedOn}
                onChange={(event) => setConsumedOn(event.target.value)}
                className={`${control} ${resting}`}
              />
            </LotField>
          ) : (
            <LotField id="lot-shelf" label={t("wine.shelf")}>
              <select
                id="lot-shelf"
                value={shelfId}
                onChange={(event) => setShelfId(event.target.value)}
                className={`${control} ${resting}`}
              >
                {storages.flatMap((storage) =>
                  storage.shelves.map((shelf) => (
                    <option key={shelf.id} value={shelf.id}>
                      {storage.name} · {shelf.name}
                    </option>
                  )),
                )}
              </select>
            </LotField>
          )}

          <LotField id="lot-date" label={t("wine.purchasedOn")}>
            <input
              id="lot-date"
              type="date"
              value={purchasedOn}
              onChange={(event) => setPurchasedOn(event.target.value)}
              className={`${control} ${resting}`}
            />
          </LotField>

          <LotField id="lot-price" label={t("wine.price")}>
            <input
              id="lot-price"
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              className={`${control} ${resting}`}
            />
          </LotField>

          <LotField id="lot-currency" label={t("wine.currency")}>
            <input
              id="lot-currency"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
              className={`${control} ${resting}`}
            />
          </LotField>

          {/* A picker rather than the dropdown of five it was until `9.11`.
              The cellar's own sources are suggested and anything else can be
              typed — Vivino, a restaurant, an importer — which is the whole of
              what the closed enum could not hold. */}
          <Picker
            id="lot-source"
            label={t("wine.source")}
            value={source}
            onChange={setSource}
            options={(suggestions?.purchaseSources ?? []).map((row) => row.name)}
            placeholder={t("wine.sourceHint")}
          />

          {/* Beside the source because it is read off the same receipt, though
              it is the wine's and not the lot's: the field shows the number
              the wine already has, and changing it here changes it there. */}
          <LotField id="lot-systembolaget-id" label={t("wine.systembolagetId")}>
            <input
              id="lot-systembolaget-id"
              value={systembolagetId ?? wine.systembolagetId ?? ""}
              onChange={(event) => setSystembolagetId(event.target.value)}
              placeholder={t("wine.systembolagetIdHint")}
              className={`${control} ${resting}`}
            />
          </LotField>

          <LotField id="lot-from" label={t("wine.receivedFrom")}>
            <input
              id="lot-from"
              value={receivedFrom}
              onChange={(event) => setReceivedFrom(event.target.value)}
              className={`${control} ${resting}`}
            />
          </LotField>

          <LotField id="lot-prompt" label={t("wine.promptedBy")}>
            <input
              id="lot-prompt"
              value={promptedBy}
              onChange={(event) => setPromptedBy(event.target.value)}
              className={`${control} ${resting}`}
            />
          </LotField>

          <label className="col-span-full flex items-baseline gap-2 text-sm">
            <input
              type="checkbox"
              checked={alreadyDrunk}
              onChange={(event) => setAlreadyDrunk(event.target.checked)}
            />
            {t("wine.alreadyDrunk")}
          </label>

          <div className="border-line col-span-full mt-2 flex items-center gap-4 border-t pt-5">
            <button type="submit" disabled={!canSubmit} className={actionButton}>
              {busy ? t("wine.saving") : t("wine.addLotAction")}
            </button>
            {/* A way back out, offered even on a wine with no lots: the form
              opened itself there, and a thing that opens itself should be
              closable by the person who did not ask for it. */}
            <button
              type="button"
              onClick={() => setAddingLot(false)}
              className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
            >
              {t("wine.cancel")}
            </button>
            {error !== null && (
              <span role="alert" className="text-past text-sm">
                {error}
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

function LotField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className={fieldLabel} htmlFor={id}>
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
